import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subject, fromEvent, merge } from 'rxjs';
import { filter, map, share, throttleTime } from 'rxjs/operators';

/**
 * Tracks the published dataset generation the UI is currently showing (GF-4823).
 *
 * `seen` is the newest version any response reported; `shown` is the one the
 * visible data was built from. They diverge exactly when a refresh is available.
 */
@Injectable({ providedIn: 'root' })
export class DatasetVersionService {
  private readonly document = inject(DOCUMENT);

  private readonly seen = signal<string | null>(null);
  private readonly shown = signal<string | null>(null);
  private readonly tenant = signal<string | null>(null);
  private readonly refetch = new Subject<void>();

  /** The generation currently rendered, or null before the first response. */
  readonly currentVersion = this.shown.asReadonly();

  /** True when a newer generation has been published than the one on screen. */
  readonly hasNewerData = computed(() => {
    const shown = this.shown();
    const seen = this.seen();
    return shown !== null && seen !== null && shown !== seen;
  });

  /**
   * Record a version reported by a response for `tenantKey`.
   *
   * A tenant switch resets the pair. Versions are per-tenant, so carrying one
   * tenant's version into another would announce a refresh that never happened.
   */
  record(version: string | null | undefined, tenantKey: string | null): void {
    if (tenantKey !== this.tenant()) {
      this.tenant.set(tenantKey);
      this.seen.set(null);
      this.shown.set(null);
    }
    if (!version) {
      return;
    }
    // Versions are publish timestamps (`YYYYMMDD-HHMMSS`), so they compare
    // lexicographically. Ignoring older ones stops a slow in-flight response
    // from rewinding `seen` and hiding a republish.
    const current = this.seen();
    if (current !== null && version < current) {
      return;
    }
    this.seen.set(version);
    if (this.shown() === null) {
      this.shown.set(version);
    }
  }

  /** Adopt the newest version — call once the page's data has been refetched. */
  acknowledge(): void {
    this.shown.set(this.seen());
  }

  /**
   * Ask subscribers to refetch.
   *
   * Deliberately does not adopt: the new generation is only on screen once the
   * refetch succeeds, so the consumer calls {@link acknowledge} then.
   */
  requestRefresh(): void {
    this.refetch.next();
  }

  /**
   * Emits when the page's analytical queries should be refetched.
   *
   * Includes returning to a backgrounded tab, which the header alone never
   * reaches. Returning to a tab fires focus *and* visibilitychange, so the
   * stream is throttled to collapse that pair into one refetch.
   *
   * Built once and shared: a per-call stream attached a fresh pair of DOM
   * listeners for every subscriber, and nothing released them.
   */
  refresh$(): Observable<'user' | 'focus'> {
    return this.refreshStream;
  }

  private readonly refreshStream: Observable<'user' | 'focus'> = merge(
    this.refetch.pipe(map(() => 'user' as const)),
    merge(
      ...(this.document.defaultView ? [fromEvent(this.document.defaultView, 'focus')] : []),
      fromEvent(this.document, 'visibilitychange').pipe(
        filter(() => this.document.visibilityState === 'visible')
      )
    ).pipe(
      throttleTime(250),
      map(() => 'focus' as const)
    )
  ).pipe(share());
}
